import {
  Container, Typography, Grid, Box, Alert,
  Button, Paper, TextField, FormControl, InputLabel, Select,
  MenuItem, Collapse, Divider, ToggleButtonGroup, ToggleButton,
  Skeleton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Tune } from '@mui/icons-material';
import GridViewIcon from '@mui/icons-material/GridView';
import TableRowsIcon from '@mui/icons-material/TableRows';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import CaseCard from '../../components/Card/Case/CaseCard';
import CaseTable from '../../components/Card/Case/CaseTable';

const ArchivedCasesView = () => {
  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);

  const [cases, setCases] = useState([]);
  const [filteredCases, setFilteredCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLoading, setUserLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOffice, setSelectedOffice] = useState('');
  const [viewMode, setViewMode] = useState('table');

  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    dateFrom: '',
    dateTo: '',
    memberName: ''
  });

  const [offices, setOffices] = useState([]);

  const fetchCurrentUser = async () => {
    try {
      setUserLoading(true);
      const { data: { user }, error } = await supabase.auth.getUser();

      if (error || !user) {
        navigate('/login');
        return;
      }

      const { data: person } = await supabase
        .from('person')
        .select('*')
        .eq('id', user.id)
        .single();

      setCurrentUser({
        id: user.id,
        email: user.email,
        ...person
      });
      setUserRole('admin');
    } catch (err) {
      setError('Error cargando usuario');
    } finally {
      setUserLoading(false);
    }
  };

  const loadArchivedCases = async () => {
    try {
      if (!currentUser) return;
      setLoading(true);

      const { data: casesData, error } = await supabase
        .from('case')
        .select('*')
        .eq('state', 'completed');
      if (error) throw error;

      const caseIds = casesData.map(c => c.id);

      const { data: allMembers } = await supabase
        .from('case_member')
        .select(`
          case_id,
          role,
          person_id,
          name_str,
          person:person_id (
            id,
            full_name,
            email
          )
        `)
        .in('case_id', caseIds);

      const membersByCase = {};
      (allMembers || []).forEach(m => {
        if (!membersByCase[m.case_id]) membersByCase[m.case_id] = [];
        membersByCase[m.case_id].push(m);
      });

      const { data: allTasks } = await supabase
        .from('task')
        .select('*')
        .in('case_id', caseIds);

      const tasksByCase = {};
      (allTasks || []).forEach(t => {
        if (!tasksByCase[t.case_id]) tasksByCase[t.case_id] = [];
        tasksByCase[t.case_id].push(t);
      });

      // Fetch office names for the filter
      const uniqueOfficeIds = [...new Set(casesData.map(c => c.office_id).filter(Boolean))];
      const { data: officesData } = await supabase
        .from('office')
        .select('id, name')
        .in('id', uniqueOfficeIds);
      setOffices(officesData || []);

      const enriched = casesData.map(c => {
        const members = membersByCase[c.id] || [];
        const tasks = tasksByCase[c.id] || [];

        return {
          ...c,
          members: {
            client: members.find(m => m.role === 'client'),
            lawyer: members.find(m => m.role === 'lawyer'),
            partners: members.filter(m => m.role === 'partner')
          },
          tasks,
          progress: tasks.length
            ? Math.round((tasks.filter(t => t.completed).length / tasks.length) * 100)
            : 0
        };
      });

      setCases(enriched);
    } catch (err) {
      console.error(err);
      setError('Error cargando archivo');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUser) {
      loadArchivedCases();
    }
  }, [currentUser]);

  // Filtering effect — mirrors OfficeHomeView logic
  useEffect(() => {
    if (loading) return;

    if (cases.length > 0) setFilterLoading(true);

    const timeoutId = setTimeout(() => {
      let filtered = cases;

      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        filtered = filtered.filter(exp => {
          const matchesBasic =
            exp.title?.toLowerCase().includes(searchLower) ||
            exp.number?.toLowerCase().includes(searchLower);
          const matchesClient =
            exp.members.client?.person?.full_name?.toLowerCase().includes(searchLower) ||
            exp.members.client?.name_str?.toLowerCase().includes(searchLower);
          const matchesLawyer =
            exp.members.lawyer?.person?.full_name?.toLowerCase().includes(searchLower);
          const matchesPartners = exp.members.partners?.some(p =>
            p.person?.full_name?.toLowerCase().includes(searchLower) ||
            p.name_str?.toLowerCase().includes(searchLower)
          );
          return matchesBasic || matchesClient || matchesLawyer || matchesPartners;
        });
      }

      if (selectedOffice) {
        filtered = filtered.filter(exp => exp.office_id === selectedOffice);
      }

      if (advancedFilters.dateFrom) {
        filtered = filtered.filter(exp => exp.start_date >= advancedFilters.dateFrom);
      }

      if (advancedFilters.dateTo) {
        filtered = filtered.filter(exp => exp.end_date <= advancedFilters.dateTo);
      }

      if (advancedFilters.memberName) {
        const memberSearch = advancedFilters.memberName.toLowerCase();
        filtered = filtered.filter(exp => {
          const matchesClientOrLawyer =
            exp.members.client?.person?.full_name?.toLowerCase().includes(memberSearch) ||
            exp.members.client?.name_str?.toLowerCase().includes(memberSearch) ||
            exp.members.lawyer?.person?.full_name?.toLowerCase().includes(memberSearch);
          const matchesPartners = exp.members.partners?.some(p =>
            p.person?.full_name?.toLowerCase().includes(memberSearch) ||
            p.name_str?.toLowerCase().includes(memberSearch)
          );
          return matchesClientOrLawyer || matchesPartners;
        });
      }

      setFilteredCases(filtered);
      setFilterLoading(false);
    }, cases.length > 100 ? 300 : 0);

    return () => clearTimeout(timeoutId);
  }, [cases, searchTerm, selectedOffice, advancedFilters, loading]);

  const isInitialLoading = loading || userLoading;

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (isInitialLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Skeleton variant="rectangular" width={150} height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" width="100%" height={120} sx={{ mb: 4, borderRadius: 2 }} />
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map(item => (
            <Grid item size={{ xs: 12, md: 6, lg: 4 }} key={item}>
              <Skeleton variant="rectangular" width="100%" height={280} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      {/* Header */}
      <Box mb={2}>
        <Typography variant="h5" fontWeight="bold">
          Repositorio de Expedientes
        </Typography>
      </Box>

      {/* Search & Filters Panel */}
      <Paper elevation={1} sx={{ p: 2, mb: 2, backgroundColor: '#F5F5F5' }}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <SearchIcon color="action" />
          <Typography variant="h6">Buscador de Expedientes</Typography>
          <Box sx={{ ml: 'auto' }}>
            <Button
              size="small"
              variant={showAdvancedSearch ? 'contained' : 'outlined'}
              onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
              startIcon={<Tune />}
            >
              {showAdvancedSearch ? 'Búsqueda simple' : 'Búsqueda avanzada'}
            </Button>
          </Box>
        </Box>

        {/* Basic filters */}
        <Grid container spacing={2} alignItems="center">
          <Grid item size={{ xs: 12, md: showAdvancedSearch ? 12 : 8 }}>
            <TextField
              fullWidth
              placeholder="Buscar por título, número o miembro..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />
              }}
            />
          </Grid>

          {!showAdvancedSearch && (
            <Grid item size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Despacho</InputLabel>
                <Select
                  value={selectedOffice}
                  onChange={(e) => setSelectedOffice(e.target.value)}
                  label="Despacho"
                >
                  <MenuItem value="">Todos los despachos</MenuItem>
                  {offices.map(o => (
                    <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>

        {/* Advanced search panel */}
        <Collapse in={showAdvancedSearch}>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Grid item size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Despacho</InputLabel>
                <Select
                  value={selectedOffice}
                  onChange={(e) => setSelectedOffice(e.target.value)}
                  label="Despacho"
                >
                  <MenuItem value="">Todos</MenuItem>
                  {offices.map(o => (
                    <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                value={advancedFilters.dateFrom}
                onChange={(e) => setAdvancedFilters({ ...advancedFilters, dateFrom: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                value={advancedFilters.dateTo}
                onChange={(e) => setAdvancedFilters({ ...advancedFilters, dateTo: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Buscar por nombre de miembro"
                placeholder="Cliente, abogado, colaborador..."
                value={advancedFilters.memberName}
                onChange={(e) => setAdvancedFilters({ ...advancedFilters, memberName: e.target.value })}
              />
            </Grid>

            <Grid item size={{ xs: 12, md: 6 }}>
              <Box display="flex" gap={1} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => {
                    setAdvancedFilters({ dateFrom: '', dateTo: '', memberName: '' });
                    setSearchTerm('');
                    setSelectedOffice('');
                  }}
                >
                  Limpiar filtros
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setShowAdvancedSearch(false)}
                >
                  Aplicar filtros
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Collapse>

        {/* Results summary */}
        {!loading && !filterLoading && (
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
            <Typography variant="body2" color="text.secondary">
              Mostrando {filteredCases.length} de {cases.length} expedientes finalizados
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Title row + view toggle */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold">
          Expedientes Finalizados
        </Typography>

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, value) => value && setViewMode(value)}
          size="small"
        >
          <ToggleButton value="grid">
            <GridViewIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="table">
            <TableRowsIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Content */}
      {filterLoading ? (
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map(item => (
            <Grid item size={{ xs: 12, md: 6, lg: 4 }} key={item}>
              <Skeleton variant="rectangular" width="100%" height={280} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      ) : filteredCases.length === 0 ? (
        <Paper elevation={1} sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            📦 No hay expedientes finalizados
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchTerm || selectedOffice
              ? 'Intenta ajustar los filtros de búsqueda'
              : 'No se encontraron expedientes finalizados'
            }
          </Typography>
        </Paper>
      ) : viewMode === 'grid' ? (
        <Grid container spacing={3}>
          {filteredCases.map((expedient) => (
            <CaseCard
              key={expedient.id}
              onViewDetails={() => navigate(`/office/${expedient.office_id}/case/${expedient.id}`)}
              expedient={expedient}
              userRoleInCase={expedient.userRoleInCase}
              showRoleBadge={false}
              officeColor={expedient.office?.color}
            />
          ))}
        </Grid>
      ) : (
        <CaseTable
          expedients={filteredCases}
          onViewDetails={(id) => {
            const exp = filteredCases.find(c => c.id === id);
            navigate(`/office/${exp.office_id}/case/${id}`);
          }}
        />
      )}

      {/* Bottom summary */}
      {!isInitialLoading && !filterLoading && filteredCases.length > 0 && (
        <Box mt={4} textAlign="center">
          <Typography variant="body2" color="text.secondary">
            Mostrando {filteredCases.length} de {cases.length} expedientes finalizados
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default ArchivedCasesView;